import { useEffect, useRef, useState } from "react";

type UseScrollVisibilityOptions = {
  /**
   * Direction to show the element
   * - "down": Show when scrolling down, hide when scrolling up
   * - "up": Show when scrolling up, hide when scrolling down
   */
  showOnScroll?: "down" | "up";
  /**
   * Show when at the top of the page
   * @default true
   */
  showAtTop?: boolean;
  /**
   * Show when at the bottom of the page
   * @default false
   */
  showAtBottom?: boolean;
  /**
   * Scroll threshold in pixels to prevent jitter
   * @default 5
   */
  threshold?: number;
  /**
   * Initial visibility state
   * @default true
   */
  initialVisible?: boolean;
};

/**
 * Hook to manage element visibility based on scroll direction
 * @param options Configuration options
 * @returns Boolean indicating if element should be visible
 */
export function useScrollVisibility(
  options: UseScrollVisibilityOptions = {},
): boolean {
  const {
    showOnScroll = "down",
    showAtTop = true,
    showAtBottom = false,
    threshold = 5,
    initialVisible = true,
  } = options;

  const [isVisible, setIsVisible] = useState(initialVisible);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY || window.pageYOffset;
          const scrollDifference = currentScrollY - lastScrollY.current;

          // Check if at top or bottom first (these take priority)
          const isAtTop = currentScrollY === 0;
          // Determine if user has scrolled to the bottom of the page.
          const isAtBottom =
            window.innerHeight + currentScrollY >=
            document.documentElement.scrollHeight - 100;

          // Check priority conditions first (at top/bottom)
          if (showAtTop && isAtTop) {
            setIsVisible(true);
          } else if (showAtBottom && isAtBottom) {
            setIsVisible(true);
          } else {
            // Determine visibility based on scroll direction
            if (showOnScroll === "down") {
              // Show when scrolling down, hide when scrolling up
              if (scrollDifference > threshold) {
                setIsVisible(true);
              } else if (scrollDifference < -threshold) {
                setIsVisible(false);
              }
            } else {
              // Show when scrolling up, hide when scrolling down
              if (scrollDifference < -threshold) {
                setIsVisible(true);
              } else if (scrollDifference > threshold) {
                setIsVisible(false);
              }
            }
          }

          lastScrollY.current = currentScrollY;
          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    // Set initial scroll position
    lastScrollY.current = window.scrollY || window.pageYOffset;

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [showOnScroll, showAtTop, showAtBottom, threshold]);

  return isVisible;
}
